/* global $ */
import React, { useCallback, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { connect, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { clearError } from '@/actions';
import 'styles/components/Notification';
import useWhitelabelTheme from 'hooks/whitelabel/useWhitelabelTheme';
import useReactNativeMessage from 'hooks/useReactNativeMessage';
import { isMobileDevice } from 'utils/Helpers';

const intervals = [];

const Notification = ({
  code,
  redirect,
  style,
  message,
  customMsg,
  customRedirectPath,
  alertType,
}) => {
  const history = useHistory();
  const containerEl = useRef(null);
  const dispatch = useDispatch();
  const RNMessage = useReactNativeMessage();
  const { colors } = useWhitelabelTheme();
  const { t } = useTranslation('notifications');
  const type = style;

  const changeMobileWebViewTheme = useCallback(
    (color) => {
      if (!isMobileDevice()) return;
      let metaThemeColor = document.querySelector('meta[name="theme-color"]');
      if (!metaThemeColor) {
        metaThemeColor = document.createElement('meta');
        metaThemeColor.name = 'theme-color';
        document.head.appendChild(metaThemeColor);
      }
      RNMessage.post({ themeColor: color });
      // Actualiza el color
      metaThemeColor.setAttribute('content', color);
    },
    [RNMessage],
  );

  const clearErrors = useCallback(() => {
    $(containerEl).toggleClass('feedback-slide-down feedback-slide-up');

    intervals.push(
      window.setTimeout(() => {
        dispatch(clearError());

        let redirectPath = '/';
        if (customRedirectPath) redirectPath = customRedirectPath;
        if (redirect) {
          history.push(redirectPath);
        }
        changeMobileWebViewTheme(colors.primary);
        intervals.forEach(clearTimeout);
      }, 500),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, history, redirect, customRedirectPath]);

  useEffect(() => {
    if (code !== 0 && alertType !== 'fixed') {
      intervals.push(window.setTimeout(clearErrors, 8000, redirect));
      const rootStyles = getComputedStyle(document.documentElement);
      const primaryColor = rootStyles.getPropertyValue(`--color-${type}`);
      changeMobileWebViewTheme(primaryColor ?? colors[type]);
    }
  }, [clearErrors, code, redirect, alertType, colors, type, changeMobileWebViewTheme]);

  const handleCloseAlert = useCallback(() => {
    dispatch(clearError());
    changeMobileWebViewTheme(colors.primary);
    intervals.forEach(clearTimeout);
  }, [dispatch, changeMobileWebViewTheme, colors]);

  const handleKeyDown = useCallback(
    (event) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        handleCloseAlert();
      }
    },
    [handleCloseAlert],
  );

  if (code === 0 || alertType === 'fixed') {
    return <div />;
  }

  return (
    <div
      className="feedback-slide-down feedback-notice"
      ref={containerEl}
      onClick={handleCloseAlert}
      role="button"
      tabIndex={0}
      onKeyDown={handleKeyDown}
    >
      <div className={`${type}-notice`}>
        <div className="notice-container">
          <i className="notification-icon" />
          <p className="message">{customMsg || t(`${type}.${message}`)}</p>
        </div>
      </div>
    </div>
  );
};

Notification.propTypes = {
  code: PropTypes.number,
  message: PropTypes.string.isRequired,
  style: PropTypes.string.isRequired,
  redirect: PropTypes.bool,
  customMsg: PropTypes.string,
  customRedirectPath: PropTypes.string,
  alertType: PropTypes.string,
};

function mapStateToProps(state) {
  const { code, message, style, redirect, customMsg, customRedirectPath, alertType } =
    state.errors || {
      code: 0,
      message: '',
      style: 'error',
      redirect: true,
      customMsg: '',
      customRedirectPath: '/',
      alertType: 'global',
    };

  return { code, message, style, redirect, customMsg, customRedirectPath, alertType };
}

export default connect(mapStateToProps)(Notification);
